import React, { useEffect, useMemo, useState, useCallback, Fragment } from 'react';
import {
  Box, Typography, Paper, TextField, InputAdornment,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  TableSortLabel, IconButton, CircularProgress, Alert,
  ToggleButtonGroup, ToggleButton, Chip, Tooltip, Stack, Link,
} from '@mui/material';
import {
  Search as SearchIcon,
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
  Inventory2 as Inventory2Icon,
  OpenInNew as OpenInNewIcon,
} from '@mui/icons-material';
import { Link as RouterLink } from 'react-router-dom';
import {
  productSearchStats,
  productSearchLookup,
  productSearchJobs,
} from '../services/api';

// ─── Helpers ────────────────────────────────────────────────────────────────
const fmtInt  = (n) => (n == null ? '—' : Number(n).toLocaleString('en-US'));
const fmtDate = (d) => {
  if (!d) return '—';
  try { return new Date(d).toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' }); }
  catch { return '—'; }
};
const fmtMoney = (n) => {
  if (n == null) return '—';
  return '$' + Number(n).toLocaleString('en-US', { maximumFractionDigits: 0 });
};

const SCOPE_OPTIONS = [
  { value: 'sold',     label: 'Sold (Closed Won)', help: 'Approved D-Tools proposals on projects with at least one Closed-Won deal. Default — Tom\'s definition of "sold."' },
  { value: 'approved', label: 'All Approved',       help: 'Every approved D-Tools proposal, regardless of CRM stage.' },
  { value: 'all',      label: 'All Proposals',      help: 'Everything in D-Tools, including drafts and unapproved revisions. Inflates counts — use carefully.' },
];

// Sortable column config for the rollup table
const ROLLUP_COLS = [
  { key: 'manufacturer',  label: 'Manufacturer', align: 'left'   },
  { key: 'model',         label: 'Model',        align: 'left'   },
  { key: 'part_number',   label: 'Part #',       align: 'left'   },
  { key: 'description',   label: 'Description',  align: 'left'   },
  { key: 'total_qty',     label: 'Qty Sold',     align: 'right'  },
  { key: 'installed_qty', label: 'Qty Installed',align: 'right'  },
  { key: 'project_count', label: 'Projects',     align: 'right'  },
];

// Sortable column config for the per-product job breakdown
const JOB_COLS = [
  { key: 'project_number', label: 'Job #',          align: 'left'  },
  { key: 'project_name',   label: 'Project',        align: 'left'  },
  { key: 'client_name',    label: 'Client',         align: 'left'  },
  { key: 'sales_rep',      label: 'Sales',          align: 'left'  },
  { key: 'project_manager',label: 'PM',             align: 'left'  },
  { key: 'total_qty',      label: 'Qty Sold',       align: 'right' },
  { key: 'installed_qty',  label: 'Qty Installed',  align: 'right' },
  { key: 'bucket',         label: 'Bucket',         align: 'left'  },
];

function compareValues(a, b, dir) {
  if (a == null && b == null) return 0;
  if (a == null) return 1;
  if (b == null) return -1;
  if (typeof a === 'number' && typeof b === 'number') return dir === 'asc' ? a - b : b - a;
  const s1 = String(a).toLowerCase();
  const s2 = String(b).toLowerCase();
  if (s1 < s2) return dir === 'asc' ? -1 : 1;
  if (s1 > s2) return dir === 'asc' ? 1  : -1;
  return 0;
}

// ─── Job Breakdown Sub-row ──────────────────────────────────────────────────
function JobBreakdown({ row, scope }) {
  const [loading, setLoading] = useState(true);
  const [error, setError]     = useState(null);
  const [data, setData]       = useState(null);
  const [sortBy, setSortBy]   = useState('total_qty');
  const [sortDir, setSortDir] = useState('desc');

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    productSearchJobs({
      model:         row.model,
      manufacturer:  row.manufacturer,
      part_number:   row.part_number || '',
      scope,
    })
      .then((r) => { if (!cancelled) { setData(r.data); setLoading(false); } })
      .catch((e) => { if (!cancelled) { setError(e?.response?.data?.error || e.message); setLoading(false); } });
    return () => { cancelled = true; };
  }, [row.model, row.manufacturer, row.part_number, scope]);

  const handleSort = (key) => {
    setSortBy((prev) => {
      if (prev === key) {
        setSortDir((d) => (d === 'asc' ? 'desc' : 'asc'));
        return prev;
      }
      setSortDir(['total_qty', 'installed_qty'].includes(key) ? 'desc' : 'asc');
      return key;
    });
  };

  const projectsSorted = useMemo(() => {
    if (!data?.projects) return [];
    return [...data.projects].sort((a, b) => compareValues(a[sortBy], b[sortBy], sortDir));
  }, [data, sortBy, sortDir]);

  if (loading) return <Box sx={{ p: 2, pl: 6 }}><CircularProgress size={20} /></Box>;
  if (error)   return <Box sx={{ p: 2, pl: 6 }}><Alert severity="error">{error}</Alert></Box>;
  if (!data || projectsSorted.length === 0)
    return <Box sx={{ p: 2, pl: 6 }}><Typography variant="body2" color="text.secondary">No projects found for this scope.</Typography></Box>;

  return (
    <Box sx={{ pl: 6, pr: 2, py: 1.5, bgcolor: '#f9fafb', borderTop: '1px solid #eee' }}>
      <Stack direction="row" spacing={2} sx={{ mb: 1 }} alignItems="center">
        <Typography variant="caption" color="text.secondary">
          Total sold:&nbsp;<strong>{fmtInt(data.total_qty)}</strong>&nbsp;·&nbsp;
          Installed:&nbsp;<strong>{fmtInt(data.installed_qty)}</strong>&nbsp;·&nbsp;
          Projects:&nbsp;<strong>{fmtInt(data.project_count)}</strong>&nbsp;·&nbsp;
          Line items:&nbsp;<strong>{fmtInt(data.line_count)}</strong>
        </Typography>
      </Stack>

      <TableContainer component={Paper} variant="outlined">
        <Table size="small">
          <TableHead>
            <TableRow sx={{ '& th': { fontWeight: 700, fontSize: '0.78rem', bgcolor: '#f3f4f6' } }}>
              {JOB_COLS.map((c) => (
                <TableCell key={c.key} align={c.align} sortDirection={sortBy === c.key ? sortDir : false}>
                  <TableSortLabel
                    active={sortBy === c.key}
                    direction={sortBy === c.key ? sortDir : 'asc'}
                    onClick={() => handleSort(c.key)}
                  >
                    {c.label}
                  </TableSortLabel>
                </TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {projectsSorted.map((p) => (
              <TableRow key={p.project_id} hover>
                <TableCell>
                  <Link
                    component={RouterLink}
                    to={`/projects/${p.project_id}`}
                    underline="hover"
                    sx={{ fontFamily: 'monospace', fontWeight: 600 }}
                  >
                    {p.project_number || `#${p.project_id}`}
                  </Link>
                </TableCell>
                <TableCell>{p.project_name || '—'}</TableCell>
                <TableCell>{p.client_name || '—'}</TableCell>
                <TableCell>{p.sales_rep || '—'}</TableCell>
                <TableCell>{p.project_manager || '—'}</TableCell>
                <TableCell align="right" sx={{ fontFamily: 'monospace', fontWeight: 600 }}>
                  {fmtInt(p.total_qty)}
                </TableCell>
                <TableCell align="right" sx={{ fontFamily: 'monospace', color: p.installed_qty > 0 ? 'success.main' : 'text.disabled' }}>
                  {fmtInt(p.installed_qty)}
                </TableCell>
                <TableCell>
                  <Chip
                    size="small"
                    label={p.bucket || '—'}
                    color={p.bucket === 'active' ? 'primary' : 'default'}
                    variant="outlined"
                  />
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    </Box>
  );
}

// ─── Main page ──────────────────────────────────────────────────────────────
export default function ProductSearch() {
  const [stats, setStats]       = useState(null);
  const [query, setQuery]       = useState('');
  const [scope, setScope]       = useState('sold');
  const [results, setResults]   = useState(null);
  const [loading, setLoading]   = useState(false);
  const [error, setError]       = useState(null);
  const [expanded, setExpanded] = useState(null); // rowKey of expanded row
  const [sortBy, setSortBy]     = useState('total_qty');
  const [sortDir, setSortDir]   = useState('desc');

  // Load stats on mount
  useEffect(() => {
    productSearchStats()
      .then((r) => setStats(r.data))
      .catch(() => {});
  }, []);

  // Debounced search
  useEffect(() => {
    const q = query.trim();
    if (q.length < 2) {
      setResults(null);
      setExpanded(null);
      return;
    }
    let cancelled = false;
    setLoading(true);
    setError(null);
    const t = setTimeout(() => {
      productSearchLookup(q, scope, 200)
        .then((r) => { if (!cancelled) { setResults(r.data); setLoading(false); setExpanded(null); } })
        .catch((e) => { if (!cancelled) { setError(e?.response?.data?.error || e.message); setLoading(false); } });
    }, 250);
    return () => { cancelled = true; clearTimeout(t); };
  }, [query, scope]);

  const handleSort = useCallback((key) => {
    setSortBy((prev) => {
      if (prev === key) {
        setSortDir((d) => (d === 'asc' ? 'desc' : 'asc'));
        return prev;
      }
      setSortDir(['total_qty', 'installed_qty', 'project_count'].includes(key) ? 'desc' : 'asc');
      return key;
    });
  }, []);

  const rowsSorted = useMemo(() => {
    if (!results?.results) return [];
    return [...results.results].sort((a, b) => compareValues(a[sortBy], b[sortBy], sortDir));
  }, [results, sortBy, sortDir]);

  const totalQty       = useMemo(() => rowsSorted.reduce((s, r) => s + (r.total_qty || 0), 0), [rowsSorted]);
  const totalInstalled = useMemo(() => rowsSorted.reduce((s, r) => s + (r.installed_qty || 0), 0), [rowsSorted]);
  const totalProjects  = useMemo(() => {
    // project_count is per-product, can't naively sum (same project may have multiple products).
    // We show "max projects on any single row" as an upper bound hint instead.
    return rowsSorted.length ? Math.max(...rowsSorted.map((r) => r.project_count || 0)) : 0;
  }, [rowsSorted]);

  const scopeOption = SCOPE_OPTIONS.find((o) => o.value === scope) || SCOPE_OPTIONS[0];

  return (
    <Box sx={{ p: 3, maxWidth: 1600, mx: 'auto' }}>
      {/* Header */}
      <Stack direction="row" alignItems="center" spacing={1.5} sx={{ mb: 1 }}>
        <Inventory2Icon color="primary" />
        <Typography variant="h4" sx={{ fontWeight: 700 }}>Product Search</Typography>
      </Stack>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        Search every product sold across every project. Type a model, SKU, manufacturer, or
        description to see total quantity sold and the jobs it was sold on.
      </Typography>

      {/* Stats strip */}
      {stats && (
        <Stack direction="row" spacing={1} sx={{ mb: 2 }} flexWrap="wrap">
          <Chip size="small" variant="outlined" label={`${fmtInt(stats.line_items)} line items indexed`} />
          <Chip size="small" variant="outlined" label={`${fmtInt(stats.distinct_products)} distinct products`} />
          <Chip size="small" variant="outlined" label={`${fmtInt(stats.manufacturers)} manufacturers`} />
          <Chip size="small" variant="outlined" label={`${fmtInt(stats.proposals)} D-Tools proposals`} />
          {stats.last_synced && (
            <Chip
              size="small"
              variant="outlined"
              label={`Last synced: ${fmtDate(stats.last_synced)}`}
              color="success"
            />
          )}
        </Stack>
      )}

      {/* Search + scope */}
      <Paper sx={{ p: 2, mb: 2 }} variant="outlined">
        <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} alignItems={{ md: 'center' }}>
          <TextField
            autoFocus
            fullWidth
            placeholder="e.g. IS8, Crestron TSR-310, Atmos, in-ceiling…"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon color="action" />
                </InputAdornment>
              ),
            }}
          />
          <Tooltip title={scopeOption.help} arrow placement="top">
            <ToggleButtonGroup
              size="small"
              exclusive
              value={scope}
              onChange={(_, v) => v && setScope(v)}
            >
              {SCOPE_OPTIONS.map((o) => (
                <ToggleButton key={o.value} value={o.value}>{o.label}</ToggleButton>
              ))}
            </ToggleButtonGroup>
          </Tooltip>
        </Stack>
        <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
          Scope: <strong>{scopeOption.label}</strong> — {scopeOption.help}
        </Typography>
      </Paper>

      {/* Results */}
      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

      {loading && (
        <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
          <CircularProgress />
        </Box>
      )}

      {!loading && results && rowsSorted.length === 0 && (
        <Alert severity="info">
          No products matched <strong>"{results.q}"</strong> in scope <strong>{scopeOption.label}</strong>.
          Try a shorter term, switch scope, or check spelling.
        </Alert>
      )}

      {!loading && rowsSorted.length > 0 && (
        <Paper variant="outlined">
          {/* Totals strip */}
          <Box sx={{ p: 1.5, bgcolor: '#f8fafc', borderBottom: '1px solid #e5e7eb' }}>
            <Stack direction="row" spacing={3} flexWrap="wrap">
              <Typography variant="body2">
                <strong>{fmtInt(rowsSorted.length)}</strong> product{rowsSorted.length === 1 ? '' : 's'} matched
              </Typography>
              <Typography variant="body2">
                Total qty sold (across all matched products):&nbsp;<strong>{fmtInt(totalQty)}</strong>
              </Typography>
              <Typography variant="body2" color="success.main">
                Installed:&nbsp;<strong>{fmtInt(totalInstalled)}</strong>
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Most projects on any one product:&nbsp;<strong>{fmtInt(totalProjects)}</strong>
              </Typography>
            </Stack>
          </Box>

          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow sx={{ '& th': { fontWeight: 700, fontSize: '0.8rem', bgcolor: '#f3f4f6' } }}>
                  <TableCell sx={{ width: 40 }} />
                  {ROLLUP_COLS.map((c) => (
                    <TableCell key={c.key} align={c.align} sortDirection={sortBy === c.key ? sortDir : false}>
                      <TableSortLabel
                        active={sortBy === c.key}
                        direction={sortBy === c.key ? sortDir : 'asc'}
                        onClick={() => handleSort(c.key)}
                      >
                        {c.label}
                      </TableSortLabel>
                    </TableCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {rowsSorted.map((row) => {
                  const key = `${row.manufacturer}|${row.model}|${row.part_number}`;
                  const isOpen = expanded === key;
                  return (
                    <Fragment key={key}>
                      <TableRow
                        hover
                        sx={{ cursor: 'pointer', '& > *': { borderBottom: isOpen ? 'none' : undefined } }}
                        onClick={() => setExpanded(isOpen ? null : key)}
                      >
                        <TableCell>
                          <IconButton size="small">
                            {isOpen ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                          </IconButton>
                        </TableCell>
                        <TableCell sx={{ fontWeight: 600 }}>{row.manufacturer}</TableCell>
                        <TableCell sx={{ fontFamily: 'monospace', fontWeight: 600 }}>{row.model}</TableCell>
                        <TableCell sx={{ fontFamily: 'monospace', color: 'text.secondary' }}>
                          {row.part_number || '—'}
                        </TableCell>
                        <TableCell sx={{ color: 'text.secondary', maxWidth: 360, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                          <Tooltip title={row.description || ''} arrow>
                            <span>{row.description || '—'}</span>
                          </Tooltip>
                        </TableCell>
                        <TableCell align="right" sx={{ fontFamily: 'monospace', fontWeight: 700, fontSize: '0.95rem' }}>
                          {fmtInt(row.total_qty)}
                        </TableCell>
                        <TableCell align="right" sx={{ fontFamily: 'monospace', color: row.installed_qty > 0 ? 'success.main' : 'text.disabled' }}>
                          {fmtInt(row.installed_qty)}
                        </TableCell>
                        <TableCell align="right">{fmtInt(row.project_count)}</TableCell>
                      </TableRow>
                      {isOpen && (
                        <TableRow>
                          <TableCell colSpan={ROLLUP_COLS.length + 1} sx={{ p: 0 }}>
                            <JobBreakdown row={row} scope={scope} />
                          </TableCell>
                        </TableRow>
                      )}
                    </Fragment>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>
        </Paper>
      )}

      {!loading && !results && (
        <Paper variant="outlined" sx={{ p: 4, textAlign: 'center', bgcolor: '#fafbfc' }}>
          <SearchIcon sx={{ fontSize: 48, color: 'text.disabled', mb: 1 }} />
          <Typography variant="body1" color="text.secondary">
            Start typing to search every D-Tools line item across every project.
          </Typography>
          <Typography variant="caption" color="text.secondary">
            Try: <code>IS8</code>, <code>TSR-310</code>, <code>Crestron amp</code>, <code>shade motor</code>…
          </Typography>
        </Paper>
      )}
    </Box>
  );
}
